import { makeStyles, tokens } from "@fluentui/react-components";
import React from "react";
import {
  $cloud80,
  $cloud90,
  $greenStatus,
  $redStatus,
} from "../../../../cssjs/colors";
import {
  ResourceAvailabilityColor,
  ResourceAvailabilityLabel,
} from "../../../../enum/ResourceAvailabilityEnum";
import {
  IResource,
  IResourceDetail,
  IResourceDetails,
} from "./ResourceAvailability.types";
import ResourceProgressBar from "./ResourceProgressBar";

export type { IResourceDetail, IResourceDetails };

export interface IResourceRowProps {
  resource: IResource;
}

function getOverallStatusColor(status: string): string {
  const s = status.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (s === "OPERATIONAL") {
    return $greenStatus;
  }
  if (s === "NOTOPERATIONAL" || s === "NONOPERATIONAL") {
    return $redStatus;
  }
  return $cloud80;
}

const ResourceRow: React.FC<IResourceRowProps> = ({ resource }) => {
  const {
    title,
    total,
    inUse,
    available,
    nonOperational,
    unknown = 0,
    details,
    overallStatus,
    tooltipHeader,
    bhsStatusBucket,
  } = resource;
  const isBaggage = title.toLowerCase().includes("bhs");
  const classes = useStyles();

  // Prepend tooltipHeader only to the bucket matching its status
  const displayDetails: typeof details =
    tooltipHeader && details && bhsStatusBucket
      ? {
          inUse: details.inUse,
          available:
            bhsStatusBucket === "available"
              ? [tooltipHeader, ...details.available]
              : details.available,
          nonOperational:
            bhsStatusBucket === "nonOperational"
              ? [tooltipHeader, ...details.nonOperational]
              : details.nonOperational,
          unknown:
            bhsStatusBucket === "unknown"
              ? [tooltipHeader, ...details.unknown]
              : details.unknown,
        }
      : details;

  const availableLabel = isBaggage
    ? ResourceAvailabilityLabel.Operable
    : ResourceAvailabilityLabel.Available;

  const segments = [
    {
      pct: (inUse / total) * 100,
      color: ResourceAvailabilityColor[ResourceAvailabilityLabel.InUse],
      label: ResourceAvailabilityLabel.InUse,
      detailKey: "inUse" as const,
      count: inUse,
    },
    {
      pct: (available / total) * 100,
      color: ResourceAvailabilityColor[availableLabel],
      label: availableLabel,
      detailKey: "available" as const,
      count: available,
    },
    {
      pct: (nonOperational / total) * 100,
      color: ResourceAvailabilityColor[ResourceAvailabilityLabel.Inoperable],
      label: ResourceAvailabilityLabel.Inoperable,
      detailKey: "nonOperational" as const,
      count: nonOperational,
    },
    {
      pct: (unknown / total) * 100,
      color: ResourceAvailabilityColor[ResourceAvailabilityLabel.Unknown],
      label: ResourceAvailabilityLabel.Unknown,
      detailKey: "unknown" as const,
      count: unknown,
    },
  ].filter((s) => s.pct > 0);

  const legendItems = [
    {
      label: ResourceAvailabilityLabel.InUse,
      value: inUse,
      color: ResourceAvailabilityColor[ResourceAvailabilityLabel.InUse],
    },
    {
      label: availableLabel,
      value: available,
      color: ResourceAvailabilityColor[availableLabel],
    },
    {
      label: ResourceAvailabilityLabel.Inoperable,
      value: nonOperational,
      color: ResourceAvailabilityColor[ResourceAvailabilityLabel.Inoperable],
    },
    {
      label: ResourceAvailabilityLabel.Unknown,
      value: unknown,
      color: ResourceAvailabilityColor[ResourceAvailabilityLabel.Unknown],
    },
  ].filter((item) => item.value > 0);

  return (
    <article className={classes.resourceRow} data-testid="resource-row">
      <div className={classes.rowHeader}>
        <div className={classes.rowTitleGroup}>
          <span className={classes.rowTitle}>{title}</span>
          {overallStatus && (
            <>
              <span className={classes.rowOverallStatus}>: </span>
              <span
                className={classes.rowOverallStatus}
                style={{ color: getOverallStatusColor(overallStatus) }}
                data-testid="overall-status"
              >
                {overallStatus}
              </span>
            </>
          )}
        </div>
        <span className={classes.rowTotal}>Total : {total}</span>
      </div>

      <ResourceProgressBar segments={segments} details={displayDetails} />

      <div className={classes.legend}>
        {legendItems.map((item) => (
          <div key={item.label} className={classes.legendItem}>
            <span
              className={classes.legendBox}
              style={{ backgroundColor: item.color }}
            ></span>
            {item.value} {item.label}
          </div>
        ))}
      </div>
    </article>
  );
};

export default ResourceRow;

const useStyles = makeStyles({
  resourceRow: {
    marginBottom: "5px",
    ":last-child": {
      marginBottom: "0",
    },
  },
  rowHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  rowTitleGroup: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  rowTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: $cloud90,
  },
  rowOverallStatus: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
  },
  rowTotal: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: $cloud80,
  },
  legend: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "6px",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    fontSize: "12px",
    fontWeight: tokens.fontWeightRegular,
    color: $cloud90,
  },
  legendBox: {
    width: "6px",
    height: "6px",
    marginRight: "6px",
    borderRadius: "2px",
  },
});
